import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createBrowserRouter, Link, RouterProvider } from 'react-router';
import { Button, EmptyState, ToastProvider, TooltipProvider } from '@zentera/ui';
import { ApiError } from '@zentera/web-core';
import { Shell } from './components/shell';
import { RequireAuth } from './lib/auth';
import { LoginPage, PasswordPage } from './pages/auth';
import { ActivityPage, HomePage, TeamPage } from './pages/admin';
import { FaqPage, HoursPage, InstitutionPage, PlansPage, PoliciesPage } from './pages/knowledge';
import { AssistantPage, DocumentsPage, TestAssistantPage } from './pages/assistant';
import { IntegrationPage } from './pages/integration';
import { ConversationsPage, DashboardPage } from './pages/attendance';
import { WebsitePage } from './pages/website';
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      retry: (count, err) => !(err instanceof ApiError && err.status >= 400 && err.status < 500) && count < 2,
    },
  },
});

const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  { path: '/password', element: <PasswordPage /> },
  {
    element: (
      <RequireAuth>
        <Shell />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <HomePage /> },
      { path: 'institution', element: <InstitutionPage /> },
      { path: 'plans', element: <PlansPage /> },
      { path: 'hours', element: <HoursPage /> },
      { path: 'policies', element: <PoliciesPage /> },
      { path: 'faq', element: <FaqPage /> },
      { path: 'documents', element: <DocumentsPage /> },
      { path: 'website', element: <WebsitePage /> },
      { path: 'assistant', element: <AssistantPage /> },
      { path: 'assistant/test', element: <TestAssistantPage /> },
      { path: 'integration', element: <IntegrationPage /> },
      { path: 'dashboard', element: <DashboardPage /> },
      { path: 'conversations/:id?', element: <ConversationsPage /> },
      { path: 'team', element: <TeamPage /> },
      { path: 'activity', element: <ActivityPage /> },
      {
        path: '*',
        element: <EmptyState title="Página não encontrada" action={<Button asChild><Link to="/">Voltar ao início</Link></Button>} />,
      },
    ],
  },
]);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider delayDuration={300}>
        <ToastProvider>
          <RouterProvider router={router} />
        </ToastProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </StrictMode>,
);
